import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  api,
  type ClubSchedule,
  type ScheduleDetailResponse,
  type ScheduleEvent,
  type SchedulesResponse,
} from "../api";
import { useClubs } from "../ClubContext";
import { ScheduleCsvImport } from "../components/ScheduleCsvImport";

const inputClass = "mt-1 w-full rounded-md border border-slate-300 px-3 py-1.5 text-sm";
const labelClass = "block text-xs font-semibold text-slate-600";

/**
 * Format YYYY-MM-DD without letting the browser guess a timezone.
 *
 * `new Date("2026-10-04")` is parsed as UTC midnight, which renders as Oct 3
 * anywhere west of Greenwich — the exact off-by-one-day bug that makes a parent
 * show up on the wrong Saturday. Splitting the parts and building a local date
 * avoids the conversion entirely.
 */
function formatDate(ymd: string): string {
  const [y, m, d] = ymd.split("-").map(Number);
  return new Date(y, m - 1, d).toLocaleDateString(undefined, {
    weekday: "short",
    month: "short",
    day: "numeric",
  });
}

/** "09:00:00" -> "9:00 AM". Null stays "TBD" — a real state, not missing data. */
function formatTime(hms: string | null): string {
  if (!hms) return "TBD";
  const [h, m] = hms.split(":").map(Number);
  const suffix = h < 12 ? "AM" : "PM";
  const hour12 = h % 12 === 0 ? 12 : h % 12;
  return `${hour12}:${String(m).padStart(2, "0")} ${suffix}`;
}

interface EventFormValues {
  eventDate: string;
  startTime: string;
  opponent: string;
  capColor: "white" | "dark";
  location: string;
  levelLabel: string;
  notes: string;
}

const EMPTY_EVENT: EventFormValues = {
  eventDate: "",
  startTime: "",
  opponent: "",
  capColor: "white",
  location: "",
  levelLabel: "",
  notes: "",
};

function toEventForm(e: ScheduleEvent): EventFormValues {
  return {
    eventDate: e.eventDate,
    // The <input type="time"> wants HH:MM; the API returns HH:MM:SS.
    startTime: e.startTime ? e.startTime.slice(0, 5) : "",
    opponent: e.opponent ?? "",
    capColor: e.capColor,
    location: e.location ?? "",
    levelLabel: e.levelLabel ?? "",
    notes: e.notes ?? "",
  };
}

function EventFields({
  values,
  onChange,
}: {
  values: EventFormValues;
  onChange: <K extends keyof EventFormValues>(key: K, value: EventFormValues[K]) => void;
}) {
  return (
    <div className="grid gap-4 sm:grid-cols-3">
      <div>
        <label className={labelClass}>Date</label>
        <input
          type="date"
          required
          value={values.eventDate}
          onChange={(e) => onChange("eventDate", e.target.value)}
          className={inputClass}
        />
      </div>
      <div>
        <label className={labelClass}>
          Start time <span className="font-normal text-slate-400">optional</span>
        </label>
        <input
          type="time"
          value={values.startTime}
          onChange={(e) => onChange("startTime", e.target.value)}
          className={inputClass}
        />
        <p className="mt-1 text-xs text-slate-500">Leave blank for TBD.</p>
      </div>
      <div>
        <label className={labelClass}>Cap color</label>
        <select
          value={values.capColor}
          onChange={(e) => onChange("capColor", e.target.value as "white" | "dark")}
          className={inputClass}
        >
          <option value="white">White</option>
          <option value="dark">Dark</option>
        </select>
        <p className="mt-1 text-xs text-slate-500">What the team wears, and which side it starts on.</p>
      </div>

      <div>
        <label className={labelClass}>Opponent</label>
        <input
          type="text"
          value={values.opponent}
          onChange={(e) => onChange("opponent", e.target.value)}
          className={inputClass}
          placeholder="Danville Dolphins"
        />
      </div>
      <div>
        <label className={labelClass}>
          Location <span className="font-normal text-slate-400">optional</span>
        </label>
        <input
          type="text"
          value={values.location}
          onChange={(e) => onChange("location", e.target.value)}
          className={inputClass}
          placeholder="San Ramon Olympic Pool"
        />
      </div>
      <div>
        <label className={labelClass}>
          Level <span className="font-normal text-slate-400">optional</span>
        </label>
        <input
          type="text"
          value={values.levelLabel}
          onChange={(e) => onChange("levelLabel", e.target.value)}
          className={inputClass}
          placeholder="14U Boys"
        />
      </div>
      <div className="sm:col-span-3">
        <label className={labelClass}>
          Notes <span className="font-normal text-slate-400">optional</span>
        </label>
        <input
          type="text"
          value={values.notes}
          onChange={(e) => onChange("notes", e.target.value)}
          className={inputClass}
          placeholder="Arrive 45 minutes early"
        />
      </div>
    </div>
  );
}

export function SchedulesPage() {
  const navigate = useNavigate();
  const { activeClub, isActiveClubAdmin: isAdmin, loading: clubsLoading } = useClubs();
  const clubId = activeClub?.clubId ?? null;

  const [schedules, setSchedules] = useState<ClubSchedule[] | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [detail, setDetail] = useState<ScheduleDetailResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<{ ok: boolean; text: string } | null>(null);

  const [newName, setNewName] = useState("");
  const [newSeason, setNewSeason] = useState("");

  const [eventForm, setEventForm] = useState<EventFormValues>(EMPTY_EVENT);
  const [editingEventId, setEditingEventId] = useState<string | null>(null);
  const [savingEvent, setSavingEvent] = useState(false);

  const fail = useCallback(
    (e: unknown, fallback: string) => {
      const msg = e instanceof Error ? e.message : fallback;
      if (msg.toLowerCase().includes("unauthor")) navigate("/login", { replace: true });
      return msg;
    },
    [navigate]
  );

  const loadSchedules = useCallback(
    async (id: string) => {
      try {
        const res = await api<SchedulesResponse>(`/v1/clubs/${id}/schedules`);
        setSchedules(res.schedules);
        setError(null);
      } catch (e) {
        setError(fail(e, "Failed to load schedules"));
      }
    },
    [fail]
  );

  const loadDetail = useCallback(
    async (id: string, scheduleId: string) => {
      try {
        const res = await api<ScheduleDetailResponse>(`/v1/clubs/${id}/schedules/${scheduleId}`);
        setDetail(res);
      } catch (e) {
        setError(fail(e, "Failed to load schedule"));
      }
    },
    [fail]
  );

  useEffect(() => {
    if (!clubId) return;
    setSchedules(null);
    setSelectedId(null);
    setDetail(null);
    loadSchedules(clubId);
  }, [clubId, loadSchedules]);

  useEffect(() => {
    if (!clubId || !selectedId) return;
    setDetail(null);
    loadDetail(clubId, selectedId);
  }, [clubId, selectedId, loadDetail]);

  async function handleCreateSchedule(e: React.FormEvent) {
    e.preventDefault();
    if (!clubId) return;
    try {
      const res = await api<{ schedule: ClubSchedule }>(`/v1/clubs/${clubId}/schedules`, {
        method: "POST",
        body: JSON.stringify({ name: newName.trim(), seasonName: newSeason.trim() || undefined }),
      });
      setNewName("");
      setNewSeason("");
      await loadSchedules(clubId);
      setSelectedId(res.schedule.id);
      setMessage({ ok: true, text: `Created ${res.schedule.name}.` });
    } catch (err) {
      setMessage({ ok: false, text: fail(err, "Create failed") });
    }
  }

  async function handleSaveEvent(e: React.FormEvent) {
    e.preventDefault();
    if (!clubId || !selectedId) return;
    setSavingEvent(true);
    setMessage(null);

    // Empty strings clear a column on the API side, which is what we want for
    // an edit. On create they'd also be fine, but sending only what's filled in
    // keeps the request honest about intent.
    const body: Record<string, unknown> = {
      eventDate: eventForm.eventDate,
      capColor: eventForm.capColor,
      startTime: eventForm.startTime,
      opponent: eventForm.opponent,
      location: eventForm.location,
      levelLabel: eventForm.levelLabel,
      notes: eventForm.notes,
    };

    // Same-day games keep the order they were added in, without asking the
    // coach to think about a sort field. Only meaningful on create.
    if (!editingEventId) {
      body.sortOrder = (detail?.events ?? []).filter(
        (ev) => ev.eventDate === eventForm.eventDate
      ).length;
    }

    try {
      const path = editingEventId
        ? `/v1/clubs/${clubId}/schedules/${selectedId}/events/${editingEventId}`
        : `/v1/clubs/${clubId}/schedules/${selectedId}/events`;
      await api(path, { method: editingEventId ? "PATCH" : "POST", body: JSON.stringify(body) });
      setEventForm(EMPTY_EVENT);
      setEditingEventId(null);
      await loadDetail(clubId, selectedId);
      await loadSchedules(clubId);
    } catch (err) {
      setMessage({ ok: false, text: fail(err, "Save failed") });
    } finally {
      setSavingEvent(false);
    }
  }

  async function handleDeleteEvent(ev: ScheduleEvent) {
    if (!clubId || !selectedId) return;
    if (!window.confirm(`Remove the ${formatDate(ev.eventDate)} game${ev.opponent ? ` vs ${ev.opponent}` : ""}?`))
      return;
    try {
      await api(`/v1/clubs/${clubId}/schedules/${selectedId}/events/${ev.id}`, {
        method: "DELETE",
      });
      if (editingEventId === ev.id) {
        setEditingEventId(null);
        setEventForm(EMPTY_EVENT);
      }
      await loadDetail(clubId, selectedId);
      await loadSchedules(clubId);
    } catch (err) {
      setMessage({ ok: false, text: fail(err, "Delete failed") });
    }
  }

  async function handlePublish(published: boolean) {
    if (!clubId || !selectedId) return;
    setMessage(null);
    try {
      await api(`/v1/clubs/${clubId}/schedules/${selectedId}/publish`, {
        method: "POST",
        body: JSON.stringify({ published }),
      });
      await loadDetail(clubId, selectedId);
      await loadSchedules(clubId);
      setMessage({
        ok: true,
        text: published ? "Published." : "Moved back to draft.",
      });
    } catch (err) {
      setMessage({ ok: false, text: fail(err, "Publish failed") });
    }
  }

  async function handleDeleteSchedule(s: ClubSchedule) {
    if (!clubId) return;
    if (
      !window.confirm(
        `Delete "${s.name}" and its ${s.eventCount ?? 0} game(s)? This can't be undone.`
      )
    )
      return;
    try {
      await api(`/v1/clubs/${clubId}/schedules/${s.id}`, { method: "DELETE" });
      setSelectedId(null);
      setDetail(null);
      await loadSchedules(clubId);
      setMessage({ ok: true, text: `Deleted ${s.name}.` });
    } catch (err) {
      setMessage({ ok: false, text: fail(err, "Delete failed") });
    }
  }

  if (!isAdmin && !clubsLoading) {
    return (
      <main className="mx-auto max-w-6xl px-6 py-8">
        <p className="text-sm text-slate-500">
          You need to be a club admin of {activeClub?.clubName ?? "this club"} to manage
          schedules.
        </p>
      </main>
    );
  }

  const schedule = detail?.schedule;
  const events = detail?.events ?? [];

  return (
    <main className="mx-auto max-w-6xl space-y-8 px-6 py-8">
      <section>
        <h2 className="mb-2 text-lg font-semibold">
          Schedules{activeClub && ` — ${activeClub.clubName}`}
        </h2>
        <p className="mb-4 text-sm text-slate-500">
          Your club's own season schedules. Publishing marks one ready to share with families;
          the public schedule pages are the next piece of work.
        </p>
        {error && <p className="mb-4 text-sm text-rose-600">{error}</p>}
        {message && (
          <p className={`mb-4 text-sm ${message.ok ? "text-emerald-600" : "text-rose-600"}`}>
            {message.text}
          </p>
        )}

        {schedules === null && !error && <p className="text-sm text-slate-500">Loading…</p>}
        {schedules && schedules.length === 0 && (
          <p className="text-sm text-slate-500">No schedules yet. Create one below.</p>
        )}
        {schedules && schedules.length > 0 && (
          <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white">
            <table className="w-full text-sm">
              <thead className="border-b border-slate-200 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">
                <tr>
                  <th className="px-4 py-2">Schedule</th>
                  <th className="px-4 py-2">Season</th>
                  <th className="px-4 py-2">Status</th>
                  <th className="px-4 py-2 text-right">Games</th>
                  <th className="px-4 py-2"></th>
                </tr>
              </thead>
              <tbody>
                {schedules.map((s) => (
                  <tr
                    key={s.id}
                    className={`border-t border-slate-100 ${selectedId === s.id ? "bg-brand-50" : ""}`}
                  >
                    <td className="px-4 py-3 font-medium">{s.name}</td>
                    <td className="px-4 py-3 text-slate-600">{s.seasonName ?? "—"}</td>
                    <td className="px-4 py-3">
                      <span
                        className={`rounded-full px-2 py-0.5 text-xs font-semibold ${
                          s.status === "published"
                            ? "bg-emerald-100 text-emerald-700"
                            : "bg-slate-100 text-slate-600"
                        }`}
                      >
                        {s.status === "published" ? "Published" : "Draft"}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right tabular-nums text-slate-600">
                      {s.eventCount ?? 0}
                    </td>
                    <td className="whitespace-nowrap px-4 py-3 text-right">
                      <button
                        onClick={() => setSelectedId(s.id === selectedId ? null : s.id)}
                        className="text-xs font-medium text-brand-700 hover:underline"
                      >
                        {s.id === selectedId ? "Close" : "Open"}
                      </button>
                      <button
                        onClick={() => handleDeleteSchedule(s)}
                        className="ml-3 text-xs font-medium text-rose-600 hover:underline"
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {selectedId && (
        <section>
          <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
            <h2 className="text-lg font-semibold">
              {schedule?.name ?? "Schedule"}
              {schedule?.seasonName && (
                <span className="ml-2 text-sm font-normal text-slate-500">
                  {schedule.seasonName}
                </span>
              )}
            </h2>
            {schedule && (
              <button
                onClick={() => handlePublish(schedule.status !== "published")}
                className={
                  schedule.status === "published"
                    ? "rounded-md border border-slate-300 px-4 py-1.5 text-sm font-medium text-slate-700"
                    : "rounded-md bg-brand-600 px-4 py-1.5 text-sm font-semibold text-white hover:bg-brand-700"
                }
              >
                {schedule.status === "published" ? "Unpublish" : "Publish"}
              </button>
            )}
          </div>

          {detail === null && <p className="text-sm text-slate-500">Loading…</p>}
          {detail && (
            <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white">
              <table className="w-full text-sm">
                <thead className="border-b border-slate-200 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">
                  <tr>
                    <th className="px-4 py-2">Date</th>
                    <th className="px-4 py-2">Time</th>
                    <th className="px-4 py-2">Opponent</th>
                    <th className="px-4 py-2">Caps</th>
                    <th className="px-4 py-2">Location</th>
                    <th className="px-4 py-2">Level</th>
                    <th className="px-4 py-2"></th>
                  </tr>
                </thead>
                <tbody>
                  {events.length === 0 && (
                    <tr>
                      <td colSpan={7} className="px-4 py-6 text-sm text-slate-500">
                        No games yet. Add one below — a schedule can't be published while it's
                        empty.
                      </td>
                    </tr>
                  )}
                  {events.map((ev) => (
                    <tr
                      key={ev.id}
                      className={`border-t border-slate-100 ${
                        editingEventId === ev.id ? "bg-brand-50" : ""
                      }`}
                    >
                      <td className="whitespace-nowrap px-4 py-3 font-medium">
                        {formatDate(ev.eventDate)}
                      </td>
                      <td className="whitespace-nowrap px-4 py-3 text-slate-600">
                        {formatTime(ev.startTime)}
                      </td>
                      <td className="px-4 py-3">
                        {ev.opponent ?? "—"}
                        {ev.notes && (
                          <span className="block text-xs text-slate-500">{ev.notes}</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-slate-600">
                        {ev.capColor === "white" ? "White" : "Dark"}
                      </td>
                      <td className="px-4 py-3 text-slate-600">{ev.location ?? "—"}</td>
                      <td className="px-4 py-3 text-slate-600">{ev.levelLabel ?? "—"}</td>
                      <td className="whitespace-nowrap px-4 py-3 text-right">
                        <button
                          onClick={() => {
                            setEditingEventId(ev.id);
                            setEventForm(toEventForm(ev));
                          }}
                          className="text-xs font-medium text-brand-700 hover:underline"
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => handleDeleteEvent(ev)}
                          className="ml-3 text-xs font-medium text-rose-600 hover:underline"
                        >
                          Remove
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {detail && (
            <form
              onSubmit={handleSaveEvent}
              className="mt-6 rounded-xl border border-slate-200 bg-white p-4"
            >
              <h3 className="mb-4 text-sm font-semibold">
                {editingEventId ? "Edit game" : "Add a game"}
              </h3>
              <EventFields
                values={eventForm}
                onChange={(key, value) => setEventForm((v) => ({ ...v, [key]: value }))}
              />
              <div className="mt-4 flex items-center gap-3">
                <button
                  type="submit"
                  disabled={savingEvent}
                  className="rounded-md bg-brand-600 px-4 py-1.5 text-sm font-semibold text-white hover:bg-brand-700 disabled:opacity-50"
                >
                  {savingEvent ? "Saving…" : editingEventId ? "Save game" : "Add game"}
                </button>
                {editingEventId && (
                  <button
                    type="button"
                    onClick={() => {
                      setEditingEventId(null);
                      setEventForm(EMPTY_EVENT);
                    }}
                    className="rounded-md border border-slate-300 px-4 py-1.5 text-sm font-medium text-slate-700"
                  >
                    Cancel
                  </button>
                )}
              </div>
            </form>
          )}

          {detail && clubId && (
            <div className="mt-6">
              <ScheduleCsvImport
                clubId={clubId}
                scheduleId={selectedId}
                existingEvents={events}
                onImported={async () => {
                  await loadDetail(clubId, selectedId);
                  await loadSchedules(clubId);
                }}
              />
            </div>
          )}
        </section>
      )}

      {!selectedId && (
        <section>
          <h2 className="mb-4 text-lg font-semibold">New schedule</h2>
          <form
            onSubmit={handleCreateSchedule}
            className="rounded-xl border border-slate-200 bg-white p-4"
          >
            <div className="flex flex-col gap-3 sm:flex-row sm:items-end">
              <div className="flex-1">
                <label className={labelClass}>Name</label>
                <input
                  type="text"
                  required
                  value={newName}
                  onChange={(e) => setNewName(e.target.value)}
                  className={inputClass}
                  placeholder="14U Boys"
                />
              </div>
              <div className="flex-1">
                <label className={labelClass}>
                  Season <span className="font-normal text-slate-400">optional</span>
                </label>
                <input
                  type="text"
                  value={newSeason}
                  onChange={(e) => setNewSeason(e.target.value)}
                  className={inputClass}
                  placeholder="Fall 2026"
                />
              </div>
              <button
                type="submit"
                className="rounded-md bg-brand-600 px-4 py-1.5 text-sm font-semibold text-white hover:bg-brand-700"
              >
                Create schedule
              </button>
            </div>
          </form>
        </section>
      )}
    </main>
  );
}
